import { all, call, put, retry, select, takeEvery } from 'redux-saga/effects';
import ACTIONS from '../app.constants';
import { API_CONFIGURATIONS, Api } from '../services/services';
import { getAccessToken } from './authentication.sagas';
import {
    PersonUpdateActionType,
    Person,
    Contact,
    AddPersonContactActionType,
    UpdatePersonContactActionType,
    PersonTypeConfiguration,
    AddPersonActionType,
    AppointmentUpdateActionType,
    DeleteAppointmentActionType,
    DeleteContactActionType,
    AddPersonCommentActionType,
    AddAppointmentCommentActionType,
    PersonTypeAction,
    ContactType,
    EventSeries,
} from '../modules/person/types';
import {
    LicensesActionType,
    Form,
    LicenseList,
    ApplicationActionType,
    AddAppointmentType,
    Appointment,
    FormTypeConfiguration,
    AccountingTransactionList,
    AppointmentType,
    Inspection,
    DeleteInspectionActionType,
    CommentList,
    DeletePersonDocumentActionType,
    mergePersonDocumentsActionType,
    AuditEventActionType,
    AuditHistoryList,
} from '../modules/application/types';
import { BASE_URL } from '../../routes';
import { personActions, siteActions, calendarActions } from '../actions';
import defaultEditForm from '../../views/PersonDetailTabs/Tabs/PersonData/defaultEditForm.json';
import addPersonTypeDefaultForm from '../../views/License/AddPersonDialog/defaultLicenseeAdd.json';
import defaultAppointmentEditForm from '../../views/AppointmentDetailTabs/Tabs/AppointmentData/appointmentDefaultEditForm.json';
import defaultAddContactForm from '../../views/PersonDetailTabs/Tabs/PersonContactDialog/defaultAddContactForm.json';
import { doubleDecodeURL } from '../../utils/utils';
import { LimitAndSkipData } from '../modules/calendar/types';
import { getAddPersonFormId } from '../selectors/site.selectors';
import {
    getAppointmentCount,
    getAppointmentInspectionCount,
    getPersonLicenseCount,
} from '../selectors/person.selectors';

function redirectToURL(history: any, location: string) {
    history.push(location);
}

function* getPerson(action: LicensesActionType) {
    const id: string = action.payload.id;
    let person: Person;
    let editForm: Form = defaultEditForm;
    let personType: PersonTypeConfiguration | undefined = undefined;

    try {
        yield put(personActions.getPersonDetail.pending);
        const personToken: string = yield call(getAccessToken, 'persons.read');
        person = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getPerson,
            personToken,
            id,
        );

        if (person.type && typeof person.type === 'object') {
            const personTypesToken: string = yield call(getAccessToken, 'personTypes.read');
            personType = yield retry(
                API_CONFIGURATIONS.MAX_RETRY_COUNT,
                API_CONFIGURATIONS.RETRY_INTERVAL,
                Api.getPersonType,
                personTypesToken,
                person.type.id,
            );
            if (personType?.views?.edit?.formId) {
                try {
                    const formsToken: string = yield call(getAccessToken, 'forms.read');
                    editForm = yield retry(
                        API_CONFIGURATIONS.MAX_RETRY_COUNT,
                        API_CONFIGURATIONS.RETRY_INTERVAL,
                        Api.getForm,
                        formsToken,
                        personType.views.edit.formId,
                    );
                } catch (error) {
                    yield put(
                        siteActions.setError({
                            isError: false,
                            errorCode: 0,
                            message: 'Something went wrong. Please contact IT support.',
                        }),
                    );
                }
            }
        }
        yield put(personActions.getPersonDetail.success(person, editForm, personType));
    } catch (error) {
        yield put(personActions.getPersonDetail.error(error));
    }
}

function* getPersonLicenses(action: LicensesActionType) {
    const id: string = action.payload.id;
    let licenseCount: number;
    try {
        const licenseToken: string = yield call(getAccessToken, 'licenses.read');
        if (action.paginationData.skip === 0) {
            licenseCount = yield retry(
                API_CONFIGURATIONS.MAX_RETRY_COUNT,
                API_CONFIGURATIONS.RETRY_INTERVAL,
                Api.getPersonLicenseCount,
                licenseToken,
                id,
            );
        } else {
            licenseCount = yield select(getPersonLicenseCount);
        }
        let personLicenses: LicenseList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getPersonLicenses,
            licenseToken,
            id,
            action?.paginationData,
        );
        yield put(personActions.getPersonLicenses.success(personLicenses, action.paginationData, licenseCount));
    } catch (error) {
        yield put(personActions.getPersonLicenses.error(error));
    }
}

function* getPersonAccounting(action: ApplicationActionType) {
    const id: string = action.payload.id;
    try {
        yield put(personActions.getPersonAccounting.pending);

        const token: string = yield call(getAccessToken, 'persons.read');

        let response: AccountingTransactionList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getPersonAccounting,
            token,
            id,
            action.paginationData,
        );
        response = response.filter((transaction) => transaction.journalEntry.invoicing?.status !== 'Void');

        yield put(personActions.getPersonAccounting.success(response, action.paginationData));
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);

        yield put(siteActions.setError(error));
        yield put(personActions.getPersonAccounting.error(error));
    }
}

function* getContactTypes() {
    try {
        yield put(personActions.getContactTypes.pending);
        const token: string = yield call(getAccessToken, 'contactTypes.read');
        const response: ContactType[] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getContactTypes,
            token,
        );
        yield put(personActions.getContactTypes.success(response));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.getContactTypes.error(error));
    }
}

function* updatePerson(action: PersonUpdateActionType) {
    const data: Person = action.payload.data;
    const personId: string = action.payload.personId;
    try {
        yield put(personActions.updatePerson.pending());
        const token: string = yield call(getAccessToken, 'persons.read');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.updatePerson,
            token,
            data,
            personId,
        );
        yield put(personActions.updatePerson.success(action.payload.data));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Information has been saved.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.updatePerson.error(error));
    }
}

function* getAppointmentList(action: ApplicationActionType) {
    const id: string = action.payload.id;
    let appointmentCount: number;
    try {
        const token: string = yield call(getAccessToken, 'events.read');
        if (action.paginationData.skip === 0) {
            // get total count of inspections
            appointmentCount = yield retry(
                API_CONFIGURATIONS.MAX_RETRY_COUNT,
                API_CONFIGURATIONS.RETRY_INTERVAL,
                Api.getAppointmentCount,
                token,
                id,
            );
        } else {
            appointmentCount = yield select(getAppointmentCount);
        }
        const response: [] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getAppointmentList,
            token,
            id,
            action?.paginationData,
        );
        yield put(
            personActions.getAppointmentList.success(
                response.length > 0 ? response : [],
                action?.paginationData,
                appointmentCount,
            ),
        );
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.getAppointmentList.error(error));
    }
}
function* getAppointmentInspectionList(action: ApplicationActionType) {
    const eventId: string = action.payload.id;
    let appointmentInspectionCount: number;

    try {
        yield put(personActions.getAppointmentInspectionList.pending);
        const token: string = yield call(getAccessToken, 'inspections.read');
        if (action.paginationData.skip === 0) {
            // get total count of inspections
            appointmentInspectionCount = yield retry(
                API_CONFIGURATIONS.MAX_RETRY_COUNT,
                API_CONFIGURATIONS.RETRY_INTERVAL,
                Api.getInspectionCount,
                token,
                { 'event.id': eventId },
            );
        } else {
            appointmentInspectionCount = yield select(getAppointmentInspectionCount);
        }
        const response: Inspection[] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getAppointmentInspection,
            token,
            eventId,
            action?.paginationData,
        );

        yield put(
            personActions.getAppointmentInspectionList.success(
                response,
                action.paginationData,
                appointmentInspectionCount,
            ),
        );
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.getAppointmentInspectionList.error(error));
    }
}

function* getAppointmentTypeList() {
    try {
        yield put(personActions.getAppointmentTypeList.pending);
        const token: string = yield call(getAccessToken, 'eventTypes.read');
        const response: AppointmentType[] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getAppointmentTypeList,
            token,
        );
        yield put(personActions.getAppointmentTypeList.success(response));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.getAppointmentTypeList.error(error));
    }
}

function* getPersonContacts(action: ApplicationActionType) {
    const id: string = action.payload.id;
    try {
        const personToken: string = yield call(getAccessToken, 'persons.read');
        const personContacts: Contact = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getPersonContacts,
            personToken,
            id,
            action?.paginationData,
        );
        yield put(personActions.getPersonContactDetail.success(personContacts, action?.paginationData));
    } catch (error) {
        yield put(personActions.getPersonContactDetail.error(error));
    }
}

function* addPersonContact(action: AddPersonContactActionType) {
    const data: Contact = action.payload.data;
    const personId: string = action.payload.personId;
    try {
        yield put(personActions.addPersonContact.pending());
        const token: string = yield call(getAccessToken, 'persons.write');
        const contact: Contact = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.addPersonContact,
            token,
            data,
            personId,
        );
        yield put(personActions.addPersonContact.success(contact));
        yield put(personActions.getPersonContactsBegin(personId, action?.paginationData));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Contact has been added.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.addPersonContact.error(error));
    }
}

function* updatePersonContact(action: UpdatePersonContactActionType) {
    const data: Contact = action.payload.data;
    const personId: string = action.payload.personId;
    const contactId: string = action.payload.contactId;
    try {
        yield put(personActions.updatePersonContact.pending());
        const token: string = yield call(getAccessToken, 'persons.write');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.updatePersonContact,
            token,
            data,
            personId,
            contactId,
        );
        yield put(personActions.updatePersonContact.success(data));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Contact has been updated.' }));
        yield put(personActions.getPersonContactsBegin(personId, action?.paginationData));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.updatePersonContact.error(error));
    }
}

function* getLicenseListAppointment(action: ApplicationActionType) {
    const id: string = action.payload.id;
    try {
        yield put(personActions.getLicenseListAppointment.pending);
        const token: string = yield call(getAccessToken, 'licenses.read');
        const response: [] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getLicenseListForAppointments,
            token,
            id,
        );
        yield put(personActions.getLicenseListAppointment.success(response.length > 0 ? response : []));
    } catch (error) {
        // TODO
        yield put(siteActions.setError(error));
        yield put(personActions.getLicenseListAppointment.error(error));
    }
}

function* addAppointment(action: AddAppointmentType) {
    const data: any = action.payload.data;
    try {
        yield put(personActions.addAppointment.pending);
        const token: string = yield call(getAccessToken, 'events.write');
        const response: Appointment = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.addAppointment,
            token,
            data,
        );
        let personId: string = '';
        action?.payload?.data?.persons.forEach((user: Person) => (personId = user.id));
        yield put(personActions.getAppointmentList.pending);
        yield put(
            personActions.getAppointmentListBegin(personId, {
                limit: 20,
                skip: 0,
            }),
        );
        yield put(personActions.addAppointment.success(response));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Appointment has been added.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.addAppointment.error(error));
    }
}

function* addAppointmentInspection(action: AddAppointmentType) {
    const data: Inspection = action.payload.data;
    try {
        yield put(personActions.addAppointmentInspection.pending);
        const token: string = yield call(getAccessToken, 'inspections.write');
        const result: Inspection = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.addInspection,
            token,
            data,
        );
        yield put(personActions.addAppointmentInspection.success(result));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Inspection has been added.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.addAppointmentInspection.error(error));
    }
}
function* addPerson(action: AddPersonActionType) {
    const personDetail: Person = action.payload.personDetail;
    const history = action.payload.history;
    try {
        yield put(personActions.addPerson.pending);
        const token: string = yield call(getAccessToken, 'persons.write');
        const result: Person = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.addPerson,
            token,
            personDetail,
        );

        yield put(personActions.addPerson.success(result));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Licensee has been added.' }));
        yield call(redirectToURL, history, BASE_URL + `/person/${result.id}/detail`);
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.addPerson.error(error));
    }
}
function* getAppointmentById(action: any) {
    const id: string = action.payload.id;
    let editForm: Form = defaultAppointmentEditForm;
    let appointment: Appointment;
    let eventFormDetails: FormTypeConfiguration;

    try {
        yield put(personActions.getAppointmentDetail.pending);
        const appointmentToken: string = yield call(getAccessToken, 'events.read');
        appointment = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getAppointmentDetail,
            appointmentToken,
            id,
        );

        if (appointment) {
            const licenseIds = appointment?.licenses?.map((ele: any) => ele?.id);
            yield put(calendarActions.getSpecificLicenses.pending);
            const token: string = yield call(getAccessToken, 'licenses.read');
            const response: [] = yield retry(
                API_CONFIGURATIONS.MAX_RETRY_COUNT,
                API_CONFIGURATIONS.RETRY_INTERVAL,
                Api.getSpecificLicenses,
                token,
                licenseIds,
            );
            yield put(calendarActions.getSpecificLicenses.success(response.length > 0 ? response : []));
        }
        if (!!appointment.type) {
            eventFormDetails = yield retry(
                API_CONFIGURATIONS.MAX_RETRY_COUNT,
                API_CONFIGURATIONS.RETRY_INTERVAL,
                Api.getEventType,
                appointmentToken,
                appointment.type?.id!,
            );

            if (eventFormDetails.views?.edit?.formId) {
                try {
                    const formsToken: string = yield call(getAccessToken, 'forms.read');
                    editForm = yield retry(
                        API_CONFIGURATIONS.MAX_RETRY_COUNT,
                        API_CONFIGURATIONS.RETRY_INTERVAL,
                        Api.getForm,
                        formsToken,
                        eventFormDetails.views?.edit?.formId,
                    );
                } catch (error) {
                    yield put(
                        siteActions.setError({
                            isError: false,
                            errorCode: 0,
                            message: 'Something went wrong. Please contact IT support.',
                        }),
                    );
                }
            }
        }

        yield put(personActions.getAppointmentDetail.success(appointment, editForm));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.getAppointmentDetail.error(error));
    }
}

function* updateAppointment(action: AppointmentUpdateActionType) {
    const data: Appointment = action.payload.data;
    const appointmentId: string = action.payload.appointmentId;
    try {
        yield put(personActions.updateAppointment.pending);
        const token: string = yield call(getAccessToken, 'events.write');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.updateAppointment,
            token,
            data,
            appointmentId,
        );
        yield put(personActions.updateAppointment.success(action.payload.data));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Appointment has been updated.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.updateAppointment.error(error));
    }
}
function* getAppointmentCommentList(action: ApplicationActionType) {
    const appointmentId: string = action.payload.id;
    const paginationData: LimitAndSkipData = action?.paginationData;
    try {
        const token: string = yield call(getAccessToken, 'events.read');
        const response: [] = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getAppointmentCommentList,
            token,
            appointmentId,
            paginationData,
        );
        yield put(personActions.getAppointmentCommentList.success(response.length > 0 ? response : [], paginationData));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.getAppointmentCommentList.error(error));
    }
}

function* addAppointmentComment(action: AddAppointmentCommentActionType) {
    const data: string = action.payload.data;
    const appointmentId: string = action.payload.appointmentId;
    try {
        yield put(personActions.addAppointmentComment.pending);
        const token: string = yield call(getAccessToken, 'events.write');
        const result: string = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.addAppointmentComment,
            token,
            data,
            appointmentId,
        );

        yield put(personActions.addAppointmentComment.success(result));
        yield put(
            siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Appointment comment has been added.' }),
        );
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.addAppointmentComment.error(error));
    }
}

function* deleteAppointmentRecord(action: DeleteAppointmentActionType) {
    const appointmentId: string = action.payload.appointmentId;
    try {
        yield put(personActions.deleteAppointmentRecord.pending);
        const token: string = yield call(getAccessToken, 'events.write');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.deleteAppointment,
            token,
            appointmentId,
        );
        yield put(personActions.deleteAppointmentRecord.success(appointmentId));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Appointment has been removed.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.deleteAppointmentRecord.error(error));
    }
}

function* deleteAppointmentInspection(action: DeleteInspectionActionType) {
    const inspectionId: string = action.payload.inspectionId;
    try {
        yield put(personActions.deleteAppointmentInspection.pending);
        const token: string = yield call(getAccessToken, 'inspections.write');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.deleteInspection,
            token,
            inspectionId,
        );
        yield put(personActions.deleteAppointmentInspection.success(doubleDecodeURL(inspectionId)));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Inspection has been removed.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.deleteAppointmentInspection.error(error));
    }
}

function* getPersonCommentList(action: ApplicationActionType) {
    const personId: string = action.payload.id;
    try {
        const token: string = yield call(getAccessToken, 'persons.read');
        const response: CommentList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getPersonCommentsList,
            token,
            personId,
            action.paginationData,
        );
        yield put(
            personActions.getPersonCommentList.success(response.length > 0 ? response : [], action.paginationData),
        );
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.getPersonCommentList.error(error));
    }
}

function* addPersonComment(action: AddPersonCommentActionType) {
    const comment: string = action.payload.comment;
    const personId: string = action.payload.personId;
    try {
        yield put(personActions.addPersonComment.pending);
        const token: string = yield call(getAccessToken, 'persons.write');
        const result: string = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.addPersonComment,
            token,
            comment,
            personId,
        );
        yield put(personActions.addPersonComment.success(result));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Person comment has been added.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.addPersonComment.error(error));
    }
}
function* deleteContactRecord(action: DeleteContactActionType) {
    const contactId: string = action.payload.contactId;
    const personId: string = action.payload.personId;
    try {
        yield put(personActions.deleteContactRecord.pending);
        const token: string = yield call(getAccessToken, 'persons.write');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.deleteContact,
            token,
            contactId,
            personId,
        );
        yield put(personActions.deleteContactRecord.success(contactId, personId));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Contact has been removed.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.deleteContactRecord.error(error));
    }
}

function* getPersonTypes() {
    try {
        yield put(personActions.getPersonTypes.pending());
        const token: string = yield call(getAccessToken, 'personTypes.read');
        const response: PersonTypeConfiguration = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getPersonTypes,
            token,
        );
        yield put(personActions.getPersonTypes.success(response));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.getPersonTypes.error(error));
    }
}

function* getPersonTypeFormById(action: PersonTypeAction) {
    const formId: string | undefined = action.payload.id;
    try {
        yield put(personActions.getPersonTypeFormById.pending());
        let PersonTypeForm: Form = addPersonTypeDefaultForm;
        const formsToken: string = yield call(getAccessToken, 'forms.read');
        if (!!formId) {
            PersonTypeForm = yield retry(
                API_CONFIGURATIONS.MAX_RETRY_COUNT,
                API_CONFIGURATIONS.RETRY_INTERVAL,
                Api.getForm,
                formsToken,
                formId,
            );
        }
        yield put(personActions.getPersonTypeFormById.success(PersonTypeForm));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.getPersonTypeFormById.error(error));
    }
}

function* getPersonFileList(action: ApplicationActionType) {
    const id: string = action.payload.id;
    try {
        yield put(personActions.getPersonFileList.pending);
        const token: string = yield call(getAccessToken, 'persons.read');
        const response = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getPersonFilesList,
            token,
            id,
            action?.paginationData,
        );
        yield put(personActions.getPersonFileList.success(response.length > 0 ? response : [], action?.paginationData));
    } catch (error) {
        yield put(personActions.getPersonFileList.error(error));
    }
}
function* deletePersonFileDocument(action: DeletePersonDocumentActionType) {
    const personId: string = action.payload.personId;
    const fileId: string = action.payload.fileId;
    try {
        yield put(personActions.deletePersonFileDocument.pending);
        const token: string = yield call(getAccessToken, 'persons.read');
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.deletePersonFileDocument,
            token,
            personId,
            fileId,
        );
        yield put(personActions.deletePersonFileDocument.success(doubleDecodeURL(fileId)));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'File has been removed.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.deletePersonFileDocument.error(error));
    }
}

function* downloadPersonFileDocument(action: mergePersonDocumentsActionType) {
    const personId: string = action.payload.personId;
    const fileId: string = action.payload.fileId;
    const fileName: string = action.payload.fileName;
    try {
        yield put(personActions.downloadPersonFileDocument.pending);
        const token: string = yield call(getAccessToken, 'persons.read');
        const fileTokenResult = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.getPersonFileToken,
            token,
            personId,
            fileId,
        );
        yield put(personActions.downloadPersonFileDocument.success);
        yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.downloadFileDocument,
            fileId,
            fileTokenResult.token,
            fileName,
        );
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.downloadPersonFileDocument.error(error));
    }
}

function* getAddContactForm() {
    let editForm: Form = defaultAddContactForm;

    try {
        yield put(personActions.getAddContactForm.pending);
        const token: string = yield call(getAccessToken, 'forms.read');
        const formId: string = yield select(getAddPersonFormId);
        if (!!formId) {
            editForm = yield retry(
                API_CONFIGURATIONS.MAX_RETRY_COUNT,
                API_CONFIGURATIONS.RETRY_INTERVAL,
                Api.getForm,
                token,
                formId,
            );
        }
        yield put(personActions.getAddContactForm.success(editForm));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.getAddContactForm.error(error));
    }
}

function* addBulkAppointment(action: AddAppointmentType) {
    const data: EventSeries = action.payload.data;

    try {
        let personId: string = '';
        action?.payload?.data?.persons.forEach((user: Person) => (personId = user.id));
        yield put(personActions.addBulkAppointment.pending);
        const token: string = yield call(getAccessToken, 'events.write');
        const response: Appointment = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT_FOR_BULK_APPOINTMENT,
            API_CONFIGURATIONS.RETRY_INTERVAL,
            Api.addBulkAppointment,
            token,
            data,
        );
        yield put(personActions.getAppointmentList.pending);
        yield put(
            personActions.getAppointmentListBegin(personId, {
                limit: 20,
                skip: 0,
            }),
        );
        yield put(personActions.addBulkAppointment.success(response));
        yield put(siteActions.setSuccess({ isError: false, errorCode: 0, message: 'Appointments have been added.' }));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.addBulkAppointment.error(error));
    }
}

function* getHistoryList(action: AuditEventActionType) {
    const id: string = action.payload.id;
    const entityType: string = action.payload.entityType;
    try {
        yield put(personActions.getHistory.pending);
        const token: string = yield call(getAccessToken, 'persons.read');
        const response: AuditHistoryList = yield retry(
            API_CONFIGURATIONS.MAX_RETRY_COUNT,
            API_CONFIGURATIONS.RETRY_INTERVAL,

            Api.getPersonHistoryList,
            token,
            id,
            entityType,
        );
        yield put(personActions.getHistory.success(response.length > 0 ? response : []));
    } catch (error) {
        yield put(siteActions.setError(error));
        yield put(personActions.getHistory.error(error));
    }
}

export default function* rootSaga() {
    yield all([
        takeEvery(ACTIONS.GET_PERSON_DETAIL_BEGIN, getPerson),
        takeEvery(ACTIONS.GET_PERSON_LICENSES_BEGIN, getPersonLicenses),
        takeEvery(ACTIONS.GET_PERSON_ACCOUNTING_BEGIN, getPersonAccounting),
        takeEvery(ACTIONS.UPDATE_PERSON_BEGIN, updatePerson),
        takeEvery(ACTIONS.ADD_PERSON_CONTACT_BEGIN, addPersonContact),
        takeEvery(ACTIONS.GET_PERSON_CONTACTS_BEGIN, getPersonContacts),
        takeEvery(ACTIONS.UPDATE_PERSON_CONTACT_BEGIN, updatePersonContact),
        takeEvery(ACTIONS.GET_APPOINTMENT_LIST_BEGIN, getAppointmentList),
        takeEvery(ACTIONS.GET_APPOINTMENT_TYPE_LIST_BEGIN, getAppointmentTypeList),
        takeEvery(ACTIONS.GET_LICENSE_LIST_APPOINTMENT_BEGIN, getLicenseListAppointment),
        takeEvery(ACTIONS.ADD_APPOINTMENT_BEGIN, addAppointment),
        takeEvery(ACTIONS.ADD_PERSON_BEGIN, addPerson),
        takeEvery(ACTIONS.GET_APPOINTMENT_DETAIL_BY_ID_BEGIN, getAppointmentById),
        takeEvery(ACTIONS.UPDATE_APPOINTMENT_BEGIN, updateAppointment),
        takeEvery(ACTIONS.DELETE_APPOINTMENT_RECORD_BEGIN, deleteAppointmentRecord),
        takeEvery(ACTIONS.DELETE_APPOINTMENT_INSPECTION_BEGIN, deleteAppointmentInspection),
        takeEvery(ACTIONS.DELETE_CONTACT_RECORD_BEGIN, deleteContactRecord),
        takeEvery(ACTIONS.GET_PERSON_COMMENT_LIST_BEGIN, getPersonCommentList),
        takeEvery(ACTIONS.ADD_PERSON_COMMENT_BEGIN, addPersonComment),
        takeEvery(ACTIONS.APPOINTMENT_COMMENT_LIST_BEGIN, getAppointmentCommentList),
        takeEvery(ACTIONS.ADD_APPOINTMENT_COMMENT_BEGIN, addAppointmentComment),
        takeEvery(ACTIONS.ADD_APPOINTMENT_INSPECTION_BEGIN, addAppointmentInspection),
        takeEvery(ACTIONS.GET_PERSON_TYPES_BEGIN, getPersonTypes),
        takeEvery(ACTIONS.GET_PERSON_TYPE_FORM_BEGIN, getPersonTypeFormById),
        takeEvery(ACTIONS.GET_CONTACT_TYPES_BEGIN, getContactTypes),
        takeEvery(ACTIONS.GET_APPOINTMENT_INSPECTION_LIST_BEGIN, getAppointmentInspectionList),
        takeEvery(ACTIONS.GET_PERSON_FILE_LIST_BEGIN, getPersonFileList),
        takeEvery(ACTIONS.DELETE_PERSON_FILE_DOCUMENT_BEGIN, deletePersonFileDocument),
        takeEvery(ACTIONS.DOWNLOAD_PERSON_FILE_DOCUMENT_BEGIN, downloadPersonFileDocument),
        takeEvery(ACTIONS.GET_ADD_CONTACT_FORM_BEGIN, getAddContactForm),
        takeEvery(ACTIONS.ADD_BULK_APPOINTMENT_BEGIN, addBulkAppointment),
        takeEvery(ACTIONS.GET_LICENSEE_HISTORY_BEGIN, getHistoryList),
    ]);
}
